import { Children, type ReactNode, isValidElement } from "react";
import { __ } from "@common/helpers/i18n";

function getLabelText(label?: ReactNode): string {
  if (typeof label === "string" || typeof label === "number") {
    return String(label).trim();
  }

  if (Array.isArray(label)) {
    return label
      .map((child) => getLabelText(child))
      .join(" ")
      .trim();
  }

  if (isValidElement(label)) {
    return Children.toArray(label.props.children)
      .map((child) => getLabelText(child))
      .join(" ")
      .trim();
  }

  return "";
}

function localized(key: string) {
  return __(key as Parameters<typeof __>[0])
    .trim()
    .toLocaleLowerCase();
}

function normalize(value: string) {
  return value.trim().toLocaleLowerCase();
}

function includesLabel(text: string, keys: string[]) {
  const normalizedText = normalize(text);

  return keys.some((key) => {
    const normalizedKey = normalize(key);
    return (
      normalizedText.includes(normalizedKey) ||
      normalizedText.includes(localized(key))
    );
  });
}

export function getFieldPlaceholder(
  label?: ReactNode,
  kind: "input" | "textarea" = "input",
  inputType?: string,
) {
  const text = getLabelText(label);

  if (!text) {
    return undefined;
  }

  if (
    inputType === "password" ||
    includesLabel(text, [
      "Password",
      "Current password",
      "New password",
      "Confirm password",
    ])
  ) {
    return __("Enter your password");
  }

  if (includesLabel(text, ["Email", "New Email", "Developer email"])) {
    return "name@example.com";
  }

  if (
    includesLabel(text, [
      "Mobile",
      "Mobile number",
      "Developer mobile",
      "Phone",
    ])
  ) {
    return __("For example, 9123456789");
  }

  if (includesLabel(text, ["Redirect uri", "Callback URL"])) {
    return "https://example.com/oauth/callback";
  }

  if (includesLabel(text, ["Webhook url"])) {
    return "https://example.com/webhook";
  }

  if (includesLabel(text, ["Website Url", "Site", "Link", "Joining URL"])) {
    return "https://example.com";
  }

  if (includesLabel(text, ["National code"])) {
    return "0012345678";
  }

  if (includesLabel(text, ["Postal code"])) {
    return "1234567890";
  }

  if (includesLabel(text, ["Address"])) {
    return __("For example, Tehran, Valiasr St.");
  }

  if (includesLabel(text, ["Name", "Full name"])) {
    return __("For example, %{site}", {
      site: import.meta.env.VITE_SITE_TITLE || import.meta.env.VITE_SITE_NAME,
    });
  }

  if (includesLabel(text, ["Title"])) {
    return __("For example, product intro");
  }

  if (includesLabel(text, ["Text"])) {
    return __("Write the text");
  }

  if (includesLabel(text, ["Description"]) || kind === "textarea") {
    return __("Write a short description");
  }

  return undefined;
}

export function getResolvedFieldPlaceholder(
  label?: ReactNode,
  placeholder?: string,
  kind: "input" | "textarea" = "input",
  inputType?: string,
) {
  const fallback = getFieldPlaceholder(label, kind, inputType);

  if (!placeholder) {
    return fallback;
  }

  const text = getLabelText(label);
  const genericPlaceholder =
    text && __("Enter %{field}", { field: text });

  if (fallback && genericPlaceholder && placeholder === genericPlaceholder) {
    return fallback;
  }

  return placeholder;
}
